import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Compass, HandHeart, KeyRound, MessageCircle, PhoneCall, ShieldCheck, Sparkles, Users, Handshake, ClipboardList } from "lucide-react";
import { CtaBand, CtaGroup } from "../components/site/Cta";
import { SectionHead } from "../components/site/Blocks";

const SERVICES = [
  { icon: HandHeart, title: "In-Home Care", text: "Our caregivers come to you, providing non-medical support so adults can stay comfortable and independent in their own homes.", to: "/in-home-care", cta: "Find Care", id: "in-home-care", img: "/images/home-living.jpg" },
  { icon: KeyRound, title: "Independent Living", text: "Housing programs for adults who live independently and want stable, comfortable accommodation in a shared home setting.", to: "/housing#independent-living", cta: "Explore Housing", id: "independent-living", img: "/images/shared-bedroom-v2.jpg" },
  { icon: Building2, title: "Assisted Living Care Home", text: "Our assisted living care home is coming soon. Join the interest list to be among the first we contact as details are confirmed.", to: "/housing#assisted-living", cta: "Join the Interest List", id: "assisted-living", img: "/images/outdoor.jpg", soon: true },
];
const INVOLVED = [
  { icon: Users, title: "Join Our Care Team", text: "Caregivers and personal-support professionals who want to make a difference in someone's everyday life.", to: "/careers", cta: "Join Our Team", id: "careers" },
  { icon: Handshake, title: "Partner With Us", text: "Case managers, social workers, housing organizations, community agencies, property owners and landlords.", to: "/partner-with-us", cta: "Partner With Us", id: "partner" },
];
const VALUES = [
  { icon: ShieldCheck, title: "Dignity", text: "Every person is supported in a way that respects their privacy, choices and sense of self." },
  { icon: MessageCircle, title: "Respect", text: "We listen first, communicate clearly and involve families and individuals in every decision." },
  { icon: Sparkles, title: "Individualized support", text: "Support is shaped around each person's routines, preferences, culture and goals." },
];
const STEPS = [
  { icon: ClipboardList, title: "Share your needs", text: "Complete a short guided questionnaire for yourself or someone you are helping." },
  { icon: PhoneCall, title: "We follow up", text: "A member of our team reviews your inquiry and contacts you personally." },
  { icon: Compass, title: "Explore options", text: "We discuss the support options and arrangements that are best for you." },
  { icon: HandHeart, title: "Plan next steps", text: "Together, we agree on a plan and the next steps that work for you." },
];

const HERO_CTAS = {
  "find-care": { label: "In-Home Care", to: "/in-home-care" },
  "explore-housing": { label: "Independent Living", to: "/housing#independent-living" },
};

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="home-hero">
    <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
    <div className="container-site pt-12 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 xl:col-span-6 relative">
        <span className="eyebrow animate-rise">Friendship Pen Care Services</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1C2421] font-semibold leading-[1.05] animate-rise delay-1" data-testid="home-headline">
          Care, Housing and Support for <em className="text-[#C86D51] not-italic font-display italic">Every Stage</em> of Life.
        </h1>
        <div className="mt-7 space-y-4 animate-rise delay-2">
          <p className="text-base sm:text-lg text-[#1C2421] leading-relaxed">
            Friendship Pen Care Services is a community care organization offering in-home care services and a range of housing programs for adults and families.
          </p>
          <p className="text-sm sm:text-base text-[#5A6561] leading-relaxed">
            Our caregivers come to you, supporting adults who want to remain comfortable in their own homes. Our independent living program offers stability and independence, and our assisted living care home is coming soon. Everything we do is centered on dignity, respect and individualized support.
          </p>
          <p className="text-sm sm:text-base text-[#5A6561] leading-relaxed">
            We also welcome qualified caregivers and community partners who share our commitment to helping people access appropriate care and housing resources.
          </p>
        </div>
        <div className="mt-9 animate-rise delay-3 xl:-mr-28"><CtaGroup prefix="hero" overrides={HERO_CTAS} /></div>
      </div>
      <div className="lg:col-span-5 xl:col-span-6 relative animate-rise delay-2">
        <div className="absolute -top-6 -right-6 h-40 w-40 rounded-full bg-[#F5E6A3]/25 blur-2xl" aria-hidden="true" />
        <div className="relative grid grid-cols-5 gap-4">
          <img src="/images/hero.jpg" alt="A Black caregiver doing a puzzle with an older Black man and an older white woman at home" className="col-span-5 sm:col-span-3 h-80 sm:h-[30rem] w-full object-cover rounded-[2rem] rounded-tr-[6rem]" />
          <div className="hidden sm:flex col-span-2 flex-col gap-4">
            <img src="/images/companion.jpg" alt="A Black caregiver laughing with an older woman on a sofa" className="h-56 w-full object-cover rounded-[1.5rem]" />
            <div className="relative flex-1 overflow-hidden rounded-[1.5rem] border border-white/70 p-5 flex flex-col justify-start backdrop-blur-md" style={{ backgroundColor: "rgba(122, 196, 182, 0.42)" }} data-testid="hero-serving-box">
              <svg viewBox="0 0 200 60" preserveAspectRatio="none" aria-hidden="true" className="absolute bottom-0 left-0 w-full h-1/2 opacity-20" fill="#14271D">
                <path d="M0 60V42h10v-8h8v8h6V28h10v14h5v-4h9v22zM48 60V30h12v-6h8v36zM70 60V36h10v24zM82 60V26h6v-4h6v38zM98 60V40h8v20zM116 60V30l2-8h-3l1.5-4h3L121 2l1.5 16h3l1.5 4h-3l2 8v30zM132 60V34h10v26zM144 60V24h12v36zM158 60V38h8v-6h6v28zM174 60V30h10v30zM186 60V40h14v20z" />
              </svg>
              <p className="relative text-xs uppercase tracking-[0.2em] text-[#14271D] font-bold">Serving</p>
              <p className="relative font-display text-lg mt-2 leading-snug text-[#14271D] font-semibold" data-testid="hero-serving-regions">The Greater Toronto Area, Ontario</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ServiceCard = ({ p }) => (
  <Link to={p.to} className="group card-warm overflow-hidden flex flex-col" data-testid={`home-service-${p.id}`}>
    <div className="relative">
      <img src={p.img} alt="" className="h-56 w-full object-cover" />
      {p.soon && <span className="absolute top-4 left-4 rounded-full bg-[#F5E6A3] text-[#1C2421] text-xs font-bold uppercase tracking-wider px-3 py-1" data-testid="home-assisted-coming-soon-badge">Coming soon</span>}
    </div>
    <div className="p-7 sm:p-8 flex flex-col flex-1">
      <p.icon className="h-6 w-6 text-[#C86D51]" aria-hidden="true" />
      <h3 className="font-display text-2xl font-semibold text-[#1C2421] mt-5">{p.title}</h3>
      <p className="text-sm text-[#5A6561] leading-relaxed mt-3 flex-1">{p.text}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A2B]">
        {p.cta}<ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </span>
    </div>
  </Link>
);

const PathCard = ({ p }) => (
  <Link to={p.to} className="group card-warm overflow-hidden flex flex-col" data-testid={`home-path-${p.id}`}>
    <div className="p-7 sm:p-8 flex flex-col flex-1">
      <p.icon className="h-6 w-6 text-[#C86D51]" aria-hidden="true" />
      <h3 className="font-display text-2xl font-semibold text-[#1C2421] mt-5">{p.title}</h3>
      <p className="text-sm text-[#5A6561] leading-relaxed mt-3 flex-1">{p.text}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A2B]">
        {p.cta}<ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </span>
    </div>
  </Link>
);

const Regions = () => (
  <section className="container-site py-16 sm:py-24" data-testid="home-regions">
    <SectionHead eyebrow="Where we work" title="Serving the Greater Toronto Area.">
      We serve clients, families, caregivers and partners across the Greater Toronto Area, Ontario.
    </SectionHead>
    <div className="grid gap-6 mt-12">
      {[["/images/toronto.jpg", "Greater Toronto Area", "Ontario, Canada", "Toronto skyline along the waterfront"]].map(([src, t, s, alt]) => (
        <div key={t} className="relative overflow-hidden rounded-[2rem] h-80 group">
          <img src={src} alt={alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14271D]/85 via-[#14271D]/20 to-transparent" />
          <div className="absolute bottom-0 p-7 text-[#FAF8F5]">
            <p className="font-display text-2xl font-semibold">{t}</p>
            <p className="text-sm text-[#E5E0D8] mt-1">{s}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <section className="bg-[#F3EFEA] border-y border-[#E5E0D8]">
        <div className="container-site py-16 sm:py-24">
          <SectionHead eyebrow="Our services" title="Three ways we support you.">
            In-home care where our caregivers come to you, independent living, and an assisted living care home coming soon.
          </SectionHead>
          <div className="grid md:grid-cols-3 gap-6 mt-12" data-testid="home-services">{SERVICES.map((p) => <ServiceCard key={p.id} p={p} />)}</div>
          <h3 className="eyebrow mt-16">Get involved</h3>
          <div className="grid md:grid-cols-2 gap-6">{INVOLVED.map((p) => <PathCard key={p.id} p={p} />)}</div>
        </div>
      </section>
      <section className="container-site py-16 sm:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionHead eyebrow="Our approach" title="Support that begins with the person.">
            Care and housing decisions are personal. We take time to understand each situation before discussing options.
          </SectionHead>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="border-t-2 border-[#C86D51] pt-6" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <v.icon className="h-6 w-6 text-[#1E3A2B]" aria-hidden="true" />
              <h3 className="font-display text-xl font-semibold mt-4">{v.title}</h3>
              <p className="text-sm text-[#5A6561] leading-relaxed mt-2">{v.text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-[#1E3A2B] text-[#FAF8F5]">
        <div className="container-site py-16 sm:py-24">
          <span className="eyebrow !text-[#F5E6A3]">What to expect</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">A clear, personal process from your first inquiry.</h2>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {STEPS.map((s, i) => (
              <li key={s.title} className="rounded-2xl border border-white/15 bg-white/5 p-6">
                <span className="font-display text-4xl text-[#F5E6A3]">0{i + 1}</span>
                <h3 className="text-lg font-semibold mt-4">{s.title}</h3>
                <p className="text-sm text-[#E5E0D8] leading-relaxed mt-2">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <Regions />
      <CtaBand prefix="home-band" />
    </>
  );
}
